/**
 * ThemeToggle — a small round button that flips between light and dark.
 *
 * Two visual variants:
 *   - default: an outlined dark-console pill (fits AdminShell / VendorShell)
 *   - light:   a subtle ghost button (fits ResidentShell's white surface)
 */
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";

export default function ThemeToggle({ variant = "default", className = "" }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const label = isDark ? "Switch to light theme" : "Switch to dark theme";

  const base = variant === "light"
    ? "border-black/5 bg-white hover:bg-zinc-100 text-zinc-700"
    : "border-white/10 bg-white/[0.02] hover:bg-white/[0.08] text-zinc-300 hover:text-white";

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      data-testid="theme-toggle"
      className={`h-9 w-9 rounded-full p-0 ${base} ${className}`}
    >
      {isDark
        ? <Sun className="h-4 w-4" aria-hidden />
        : <Moon className="h-4 w-4" aria-hidden />}
      <span className="sr-only">{label}</span>
    </Button>
  );
}
